import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, Calendar, TrendingUp, User, BarChart3, RefreshCw, AlertTriangle, CreditCard, X, Check, ExternalLink, Settings, Lock, Mail, LogOut, Eye, EyeOff, Fingerprint, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import MemberCard from '../components/MemberCard';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { currentMember, setCurrentMember, logout } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('infos'); // 'infos', 'presences' ou 'parametres'
  const [presencesStats, setPresencesStats] = useState(null);
  const [loadingPresences, setLoadingPresences] = useState(false);
  const [saisonActuelle, setSaisonActuelle] = useState(14);

  // Charger la saison actuelle dynamiquement
  useEffect(() => {
    fetch(`${API_URL}/api/saison-actuelle`)
      .then(r => r.json())
      .then(d => { if (d?.saison_actuelle) setSaisonActuelle(d.saison_actuelle); })
      .catch(() => {});
  }, []);
  const [dettes, setDettes] = useState([]);
  
  // Modal signalement de paiement
  const [showPaiementModal, setShowPaiementModal] = useState(false);

  // Sélection facture : Set de keys ("cot-13", "cot-12", "dette-{id}")
  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState(new Set());
  const [endroitPaiement, setEndroitPaiement] = useState('');
  const [modePaiement, setModePaiement] = useState('');
  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  
  // États pour les paramètres
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // États pour Face ID / Touch ID
  const [webAuthnSupported, setWebAuthnSupported] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(false);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  
  const [paiementForm, setPaiementForm] = useState({
    date_paiement: new Date().toISOString().split('T')[0],
    type: 'recette',
    objet: '',
    montant: '',
    endroit: '',
    mode_paiement: '',
    detail: ''
  });
  const [mesPaiements, setMesPaiements] = useState([]);
  const [submittingPaiement, setSubmittingPaiement] = useState(false);

  useEffect(() => {
    // NE PAS recharger le membre - utiliser celui déjà connecté
    // L'ancien code prenait members[0] ce qui écrasait le membre connecté !
    setLoading(false);
    
    // Vérifier si WebAuthn est supporté
    setWebAuthnSupported(browserSupportsWebAuthn());
  }, []);

  // Charger les passkeys du membre
  useEffect(() => {
    const loadPasskeys = async () => {
      if (currentMember?.id && webAuthnSupported) {
        setLoadingPasskeys(true);
        try {
          const response = await axios.get(`${API_URL}/api/webauthn/passkeys/${currentMember.id}`);
          setPasskeys(response.data.passkeys || []);
        } catch (error) {
          console.error('Erreur chargement passkeys:', error);
          setPasskeys([]);
        } finally {
          setLoadingPasskeys(false);
        }
      }
    };
    loadPasskeys();
  }, [currentMember, webAuthnSupported]);

  // Charger les dettes du membre
  useEffect(() => {
    const loadDettes = async () => {
      if (currentMember?.id) {
        try {
          const response = await fetch(`${API_URL}/api/dettes/membre/${currentMember.id}`);
          const data = await response.json();
          setDettes(data || []);
        } catch (error) {
          console.error('Erreur chargement dettes:', error);
          setDettes([]);
        }
      }
    };
    loadDettes();
  }, [currentMember]);

  // Charger les paiements signalés par le membre
  useEffect(() => {
    const loadMesPaiements = async () => {
      if (currentMember?.id) {
        try {
          const response = await fetch(`${API_URL}/api/paiements-en-attente/membre/${currentMember.id}`);
          const data = await response.json();
          setMesPaiements(data || []);
        } catch (error) {
          console.error('Erreur chargement paiements:', error);
          setMesPaiements([]);
        }
      }
    };
    loadMesPaiements();
  }, [currentMember]);

  // Fonction de déconnexion
  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Vous avez été déconnecté');
  };

  // Fonction de changement de mot de passe
  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membre_id: currentMember.id,
          ancien_mot_de_passe: passwordForm.currentPassword,
          nouveau_mot_de_passe: passwordForm.newPassword,
          confirmer_mot_de_passe: passwordForm.confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Mot de passe modifié avec succès !');
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data.detail || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    }
    setChangingPassword(false);
  };

  // Enregistrer un nouveau passkey (Face ID / Touch ID)
  const handleRegisterPasskey = async () => {
    if (!webAuthnSupported) {
      toast.error('Face ID / Touch ID non supporté sur cet appareil');
      return;
    }

    setRegisteringPasskey(true);
    try {
      // 1. Récupérer les options d'enregistrement
      const optionsResponse = await axios.post(`${API_URL}/api/webauthn/register/options`, {
        member_id: currentMember.id
      });

      if (!optionsResponse.data.success) {
        throw new Error('Impossible de démarrer l\'enregistrement');
      }

      // 2. Lancer l'enregistrement biométrique
      const options = JSON.parse(optionsResponse.data.options);
      const credential = await startRegistration({ optionsJSON: options });

      // 3. Vérifier avec le serveur
      const verifyResponse = await axios.post(`${API_URL}/api/webauthn/register/verify`, {
        member_id: currentMember.id,
        credential: credential
      });

      if (verifyResponse.data.success) {
        toast.success('Face ID / Touch ID activé !');
        // Recharger les passkeys
        const passkeysRes = await axios.get(`${API_URL}/api/webauthn/passkeys/${currentMember.id}`);
        setPasskeys(passkeysRes.data.passkeys || []);
      }
    } catch (error) {
      console.error('Erreur enregistrement passkey:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Enregistrement annulé');
      } else if (error.name === 'InvalidStateError') {
        toast.error('Ce passkey est déjà enregistré');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setRegisteringPasskey(false);
    }
  };

  // Supprimer un passkey
  const handleDeletePasskey = async (passkeyId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce passkey ?')) return;

    try {
      await axios.delete(`${API_URL}/api/webauthn/passkeys/${currentMember.id}/${passkeyId}`);
      toast.success('Passkey supprimé');
      setPasskeys(passkeys.filter(p => p.id !== passkeyId));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Soumettre un signalement de paiement
  const handleSubmitPaiement = async () => {
    if (!paiementForm.objet || !paiementForm.montant || !paiementForm.endroit || !paiementForm.mode_paiement) {
      toast.error('Veuillez remplir tous les champs obligatoires (caisse + mode de paiement)');
      return;
    }
    
    setSubmittingPaiement(true);
    try {
      const response = await fetch(`${API_URL}/api/paiements-en-attente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membre_id: currentMember.id,
          date_paiement: paiementForm.date_paiement,
          type: paiementForm.type,
          objet: paiementForm.objet,
          montant: parseFloat(paiementForm.montant),
          endroit: paiementForm.endroit,
          mode_paiement: paiementForm.mode_paiement,
          detail: paiementForm.detail || null
        })
      });
      
      if (response.ok) {
        toast.success('Paiement signalé ! En attente de validation par le président.');
        setShowPaiementModal(false);
        setPaiementForm({
          date_paiement: new Date().toISOString().split('T')[0],
          type: 'recette',
          objet: '',
          montant: '',
          endroit: '',
          mode_paiement: '',
          detail: ''
        });
        // Recharger les paiements
        const paiementsRes = await fetch(`${API_URL}/api/paiements-en-attente/membre/${currentMember.id}`);
        const paiementsData = await paiementsRes.json();
        setMesPaiements(paiementsData || []);
      } else {
        toast.error('Erreur lors du signalement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du signalement');
    }
    setSubmittingPaiement(false);
  };

  // Charger les stats de présences quand on va sur l'onglet
  useEffect(() => {
    const loadPresences = async () => {
      if (activeTab === 'presences' && currentMember?.id && !presencesStats) {
        setLoadingPresences(true);
        try {
          const response = await fetch(`${API_URL}/api/presences/membre/${currentMember.id}`);
          const data = await response.json();
          setPresencesStats(data);
        } catch (error) {
          console.error('Erreur chargement présences:', error);
        }
        setLoadingPresences(false);
      }
    };
    loadPresences();
  }, [activeTab, currentMember, presencesStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#D4A024] text-xl font-serif">Chargement...</div>
      </div>
    );
  }

  if (!currentMember) {
    return (
      <Card className="bg-black/40 border-2 border-[#D4A024]/30">
        <CardContent className="py-8">
          <p className="text-center text-gray-400">
            Aucun profil disponible. Veuillez créer un membre d'abord.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Générer toutes les saisons du membre
  const generateSeasons = () => {
    const currentSeason = saisonActuelle;
    const currentYear = 2012 + currentSeason;
    const startYear = currentMember.annee_entree;
    const startSeason = startYear - 2012;
    
    const seasons = [];
    for (let season = startSeason; season <= currentSeason; season++) {
      const yearStart = 2012 + season;
      const yearEnd = yearStart + 1;
      const unpaidSeasons = currentMember.situation_cotisation || 0;
      const isPaid = season <= (currentSeason - unpaidSeasons);
      
      seasons.push({
        number: season,
        label: `Saison ${season} (${yearStart}-${yearEnd})`,
        isPaid: isPaid
      });
    }
    
    return seasons;
  };

  const memberSeasons = generateSeasons();
  
  // Obtenir le % global calculé
  const getGlobalPercentage = () => {
    if (presencesStats?.totaux?.pct_global !== undefined) {
      return presencesStats.totaux.pct_global;
    }
    return currentMember.pourcentage_presences || 0;
  };

  return (
    <div className="space-y-8">
      {/* En-tête */}
      <div className="text-center md:text-left">
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-2">
          Mon Profil
        </h1>
        <p className="text-[#D4A024] text-lg font-serif">
          Vos informations et statistiques
        </p>
      </div>

      {/* Carte de membre virtuelle */}
      <div data-testid="member-card-section">
        <MemberCard member={currentMember} />
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-[#D4A024]/30 pb-2 overflow-x-auto">
        <Button
          variant={activeTab === 'infos' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('infos')}
          className={activeTab === 'infos' ? 'bg-[#D4A024] text-[#7A2020]' : 'text-gray-400 hover:text-[#D4A024]'}
          data-testid="tab-infos"
        >
          <User className="w-4 h-4 mr-2" />
          Informations
        </Button>
        <Button
          variant={activeTab === 'presences' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('presences')}
          className={activeTab === 'presences' ? 'bg-[#D4A024] text-[#7A2020]' : 'text-gray-400 hover:text-[#D4A024]'}
          data-testid="tab-presences"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Présences
        </Button>
        <Button
          variant={activeTab === 'parametres' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('parametres')}
          className={activeTab === 'parametres' ? 'bg-[#D4A024] text-[#7A2020]' : 'text-gray-400 hover:text-[#D4A024]'}
          data-testid="tab-parametres"
        >
          <Settings className="w-4 h-4 mr-2" />
          Paramètres
        </Button>
      </div>

      {activeTab === 'infos' && (
        /* Onglet Informations */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations générales */}
          <Card 
            className="lg:col-span-2 bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm" 
            data-testid="profile-info"
          >
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-white flex items-center justify-between">
                <span>{currentMember.nom_complet}</span>
                <Badge className="bg-[#7A2020] text-[#D4A024] border border-[#D4A024]">
                  {currentMember.fonction}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-[#D4A024] mt-1" />
                  <div>
                    <p className="text-base text-gray-400">Année d'entrée</p>
                    <p className="font-semibold text-lg text-white">{currentMember.annee_entree}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-[#D4A024] mt-1" />
                  <div>
                    <p className="text-base text-gray-400">Saison d'entrée</p>
                    <p className="font-semibold text-lg text-white">{currentMember.saison_entree}</p>
                  </div>
                </div>
              </div>

              {currentMember.autres_infos && (
                <div className="flex items-start space-x-3 p-4 bg-[#D4A024]/10 rounded-lg border-l-4 border-[#D4A024]">
                  <div>
                    <p className="text-base text-gray-400 mb-1">Autres informations</p>
                    <p className="text-gray-200">{currentMember.autres_infos}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistiques rapides */}
          <div className="space-y-4">
            {/* Étoiles */}
            <Card 
              className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm" 
              data-testid="profile-stars"
            >
              <CardHeader>
                <CardTitle className="text-sm font-serif text-gray-400">Évaluation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-1 mb-2">
                  {[1, 2, 3, 4].map((star) => (
                    <Star
                      key={star}
                      className={`w-8 h-8 ${
                        star <= currentMember.etoiles
                          ? 'text-[#D4A024] fill-[#D4A024]'
                          : 'text-gray-600'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-base text-gray-400">
                  {currentMember.etoiles} étoile{currentMember.etoiles > 1 ? 's' : ''}
                </p>
              </CardContent>
            </Card>

            {/* Présences (aperçu) */}
            <Card 
              className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm cursor-pointer hover:border-[#D4A024]/50 transition-colors" 
              data-testid="profile-presence"
              onClick={() => setActiveTab('presences')}
            >
              <CardHeader>
                <CardTitle className="text-sm font-serif text-gray-400">Taux de présence global</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-6 h-6 text-[#D4A024]" />
                  <span className="text-3xl font-serif font-bold text-[#D4A024]">
                    {getGlobalPercentage()}%
                  </span>
                </div>
                <p className="text-base text-gray-500 mt-2">Cliquez pour voir le détail</p>
              </CardContent>
            </Card>

            {/* Cotisation - Historique des saisons */}
            <Card 
              className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm" 
              data-testid="profile-cotisation"
            >
              <CardHeader>
                <CardTitle className="text-sm font-serif text-gray-400">
                  Historique des cotisations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {memberSeasons.map((season) => (
                    <div
                      key={season.number}
                      className={`flex items-center justify-between p-2 rounded-lg border ${
                        season.isPaid
                          ? 'bg-green-900/20 border-green-600/30'
                          : 'bg-red-900/20 border-red-600/30'
                      }`}
                    >
                      <span className={`text-sm font-medium ${
                        season.isPaid ? 'text-green-400' : 'text-red-400'
                      }`}>
                        Saison {season.number}
                      </span>
                      <div className={`w-3 h-3 rounded-full ${
                        season.isPaid ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                    </div>
                  ))}
                  
                  {/* Résumé */}
                  <div className="pt-3 mt-3 border-t border-[#D4A024]/20">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Total saisons :</span>
                      <span className="text-white font-semibold">{memberSeasons.length}</span>
                    </div>
                    {currentMember.situation_cotisation > 0 && (
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-400">En attente :</span>
                        <span className="text-red-400 font-semibold">
                          {currentMember.situation_cotisation} saison{currentMember.situation_cotisation > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Situation Financière - Facture interactive */}
            {(currentMember.situation_cotisation > 0 || dettes.length > 0) && (() => {
              const currentSeason = saisonActuelle || 14;
              // Paiements en attente (pour griser les lignes déjà déclarées)
              const pendingDetteIds = new Set(
                mesPaiements
                  .filter((p) => p.statut === 'en_attente' && p.dette_id)
                  .map((p) => p.dette_id)
              );
              const pendingCotisationsCount = mesPaiements.filter(
                (p) => p.statut === 'en_attente' && p.objet === 'cotisation'
              ).length;

              // Construire les lignes de facture
              const lignes = [];
              const nbCot = Number(currentMember.situation_cotisation || 0);
              for (let i = 0; i < nbCot; i++) {
                const saison = currentSeason - i;
                lignes.push({
                  key: `cot-${saison}`,
                  objet: 'cotisation',
                  description: `Cotisation saison ${saison}`,
                  montant: 200,
                  detail: `Cotisation saison ${saison}`,
                  // Les `pendingCotisationsCount` premières lignes sont déjà déclarées
                  pending: i < pendingCotisationsCount,
                });
              }
              dettes.forEach((d) => {
                lignes.push({
                  key: `dette-${d.id}`,
                  objet: d.cause || 'autres',
                  description: d.libelle || d.cause || 'Dette',
                  montant: Number(d.montant || 0),
                  detail: d.libelle || d.cause || 'Dette',
                  dette_id: d.id,
                  pending: pendingDetteIds.has(d.id),
                });
              });

              const totalGlobal = lignes.reduce((s, l) => s + l.montant, 0);
              const totalSelectionne = lignes
                .filter((l) => selectedInvoiceLines.has(l.key))
                .reduce((s, l) => s + l.montant, 0);

              const toggleLine = (key) => {
                const next = new Set(selectedInvoiceLines);
                if (next.has(key)) next.delete(key); else next.add(key);
                setSelectedInvoiceLines(next);
              };

              const selectableLignes = lignes.filter((l) => !l.pending);
              const toggleAll = () => {
                if (selectedInvoiceLines.size === selectableLignes.length) {
                  setSelectedInvoiceLines(new Set());
                } else {
                  setSelectedInvoiceLines(new Set(selectableLignes.map((l) => l.key)));
                }
              };

              const submitInvoice = async () => {
                const selected = lignes.filter((l) => selectedInvoiceLines.has(l.key));
                if (selected.length === 0) {
                  toast.error('Sélectionnez au moins une ligne à régler');
                  return;
                }
                if (!endroitPaiement) {
                  toast.error('Veuillez choisir la caisse créditée avant de valider');
                  return;
                }
                if (!modePaiement) {
                  toast.error('Veuillez choisir le mode de paiement avant de valider');
                  return;
                }
                setSubmittingInvoice(true);
                try {
                  await Promise.all(selected.map((l) =>
                    fetch(`${API_URL}/api/paiements-en-attente`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        membre_id: currentMember.id,
                        declarant_id: currentMember.id,
                        date_paiement: new Date().toISOString().split('T')[0],
                        type: 'recette',
                        objet: l.objet,
                        montant: l.montant,
                        endroit: endroitPaiement,
                        mode_paiement: modePaiement,
                        detail: l.detail,
                        dette_id: l.dette_id || null,
                      }),
                    })
                  ));
                  toast.success(
                    `${selected.length} paiement${selected.length > 1 ? 's' : ''} déclaré${selected.length > 1 ? 's' : ''} (${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalSelectionne)}). En attente de validation par le président.`
                  );
                  setSelectedInvoiceLines(new Set());
                  setEndroitPaiement('');
                  setModePaiement('');
                  // Recharger les paiements pour griser les lignes déclarées
                  const paiementsRes = await fetch(`${API_URL}/api/paiements-en-attente/membre/${currentMember.id}`);
                  setMesPaiements((await paiementsRes.json()) || []);
                } catch (e) {
                  toast.error('Erreur lors de la déclaration');
                } finally {
                  setSubmittingInvoice(false);
                }
              };

              return (
                <Card className="bg-black/40 border-2 border-red-600/30 backdrop-blur-sm" data-testid="situation-financiere-facture">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-serif text-white flex items-center justify-between">
                      <span className="flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2 text-red-400" />
                        Situation Financière
                      </span>
                      {selectableLignes.length > 0 && (
                        <button
                          type="button"
                          onClick={toggleAll}
                          className="text-xs text-[#D4A024] underline hover:text-[#E5B544]"
                        >
                          {selectedInvoiceLines.size === selectableLignes.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                        </button>
                      )}
                    </CardTitle>
                    <p className="text-sm text-gray-400">
                      Cochez les lignes que vous souhaitez régler. Le total se met à jour automatiquement.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* En-tête de la facture */}
                    <div className="grid grid-cols-[24px_1fr_auto] items-center gap-3 pb-2 mb-2 border-b border-red-600/30 text-xs uppercase tracking-wider text-gray-400">
                      <span></span>
                      <span>Désignation</span>
                      <span className="text-right">Montant</span>
                    </div>

                    {/* Lignes */}
                    <div className="space-y-1">
                      {lignes.map((l) => {
                        const checked = selectedInvoiceLines.has(l.key);
                        if (l.pending) {
                          return (
                            <div
                              key={l.key}
                              className="grid grid-cols-[24px_1fr_auto] items-center gap-3 py-2 px-2 rounded bg-yellow-900/20 border border-yellow-500/30"
                              data-testid={`invoice-line-${l.key}-pending`}
                            >
                              <span className="text-yellow-400 text-lg">⏳</span>
                              <span className="text-yellow-200 text-base">
                                {l.description}
                                <span className="text-xs text-yellow-400/80 ml-2">(en attente de validation)</span>
                              </span>
                              <span className="text-yellow-300 text-base font-bold">
                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(l.montant)}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <label
                            key={l.key}
                            className={`grid grid-cols-[24px_1fr_auto] items-center gap-3 py-2 px-2 rounded cursor-pointer transition-colors ${
                              checked ? 'bg-green-900/30 ring-1 ring-green-500/40' : 'hover:bg-white/5'
                            }`}
                            data-testid={`invoice-line-${l.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleLine(l.key)}
                              className="w-4 h-4 accent-green-500"
                              data-testid={`invoice-check-${l.key}`}
                            />
                            <span className={`text-base ${checked ? 'text-green-200' : 'text-gray-200'}`}>
                              {l.description}
                            </span>
                            <span className={`text-base font-bold ${checked ? 'text-green-300' : 'text-red-300'}`}>
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(l.montant)}
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    {/* Totaux */}
                    <div className="mt-4 pt-3 border-t border-red-600/30 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Total dû :</span>
                        <span className="text-gray-200 font-semibold">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalGlobal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-base">
                        <span className="text-green-300 font-semibold">Total sélectionné à régler :</span>
                        <span className="text-2xl font-bold text-green-300" data-testid="invoice-total-selected">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalSelectionne)}
                        </span>
                      </div>
                    </div>

                    {/* Caisse + Mode de paiement + bouton */}
                    {selectedInvoiceLines.size > 0 && (
                      <div className="mt-4 space-y-3">
                        <div>
                          <Label className="text-gray-300 text-sm mb-1 block">
                            Caisse créditée <span className="text-red-400">*</span>
                          </Label>
                          <select
                            value={endroitPaiement}
                            onChange={(e) => setEndroitPaiement(e.target.value)}
                            className={`w-full bg-black/40 border rounded px-3 py-2 text-white ${
                              endroitPaiement
                                ? 'border-[#D4A024]/30'
                                : 'border-red-500/60 ring-1 ring-red-500/40'
                            }`}
                            data-testid="invoice-endroit"
                            required
                          >
                            <option value="" disabled>— Où va l'argent ? —</option>
                            <option value="Compte bancaire">Compte bancaire</option>
                            <option value="Chez Fabien">Chez Fabien</option>
                            <option value="Chez Jacques">Chez Jacques</option>
                            <option value="PayPal">PayPal</option>
                            <option value="Asso Connect">Asso Connect</option>
                          </select>
                          {!endroitPaiement && (
                            <p className="text-xs text-red-400 mt-1">
                              Précisez la caisse créditée
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="text-gray-300 text-sm mb-1 block">
                            Mode de paiement <span className="text-red-400">*</span>
                          </Label>
                          <div className="grid grid-cols-4 gap-2">
                            {['Virement', 'Espèces', 'Chèque', 'CB'].map((mode) => (
                              <button
                                key={mode}
                                type="button"
                                onClick={() => setModePaiement(mode)}
                                className={`px-2 py-2 rounded border text-xs font-semibold transition ${
                                  modePaiement === mode
                                    ? 'bg-[#D4A024]/30 border-[#D4A024] text-[#D4A024]'
                                    : 'bg-black/40 border-[#D4A024]/30 text-white hover:bg-[#D4A024]/10'
                                }`}
                                data-testid={`invoice-mode-${mode}`}
                              >
                                {mode}
                              </button>
                            ))}
                          </div>
                          {!modePaiement && (
                            <p className="text-xs text-red-400 mt-1">
                              Précisez comment vous avez payé
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={submitInvoice}
                          disabled={submittingInvoice || !endroitPaiement || !modePaiement}
                          className="w-full bg-green-700 hover:bg-green-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-3"
                          data-testid="invoice-declarer-btn"
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          {submittingInvoice
                            ? 'Envoi en cours…'
                            : `Déclarer le paiement de ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalSelectionne)}`}
                        </Button>
                        <p className="text-xs text-gray-400 text-center">
                          Le président validera votre paiement et les lignes seront retirées automatiquement.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Bouton Signaler un paiement */}
            <Card className="bg-black/40 border-2 border-green-600/30 backdrop-blur-sm">
              <CardContent className="py-4">
                <Button
                  onClick={() => setShowPaiementModal(true)}
                  className="w-full bg-green-700 hover:bg-green-600 text-white font-bold py-3"
                  data-testid="btn-signaler-paiement"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Signaler un paiement
                </Button>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Signalez un paiement effectué pour qu'il soit validé par le président
                </p>
              </CardContent>
            </Card>

            {/* Liens de paiement externes */}
            <Card className="bg-black/40 border-2 border-blue-600/30 backdrop-blur-sm">
              <CardContent className="py-4">
                <h3 className="text-white font-semibold mb-3 text-center">Effectuer un paiement</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => window.open('https://www.paypal.com', '_blank')}
                    className="flex-1 bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold py-3"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Paiement PayPal
                  </Button>
                  <Button
                    onClick={() => window.open('https://www.assoconnect.com', '_blank')}
                    className="flex-1 bg-[#FF6B35] hover:bg-[#e55a2b] text-white font-bold py-3"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Paiement AssoConnect
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Connectez-vous à votre compte pour effectuer un virement au club
                </p>
              </CardContent>
            </Card>

            {/* Paiements en attente de validation */}
            {mesPaiements.length > 0 && (
              <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-serif text-white">
                    Mes paiements signalés
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {mesPaiements.map((paiement) => (
                      <div 
                        key={paiement.id} 
                        className={`p-3 rounded-lg border ${
                          paiement.statut === 'en_attente' 
                            ? 'bg-yellow-900/20 border-yellow-600/30' 
                            : paiement.statut === 'validé'
                            ? 'bg-green-900/20 border-green-600/30'
                            : 'bg-red-900/20 border-red-600/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">
                              {paiement.objet} - {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(paiement.montant)}
                            </p>
                            <p className="text-sm text-gray-400">
                              {paiement.date_paiement} • Caisse : {paiement.endroit}
                              {paiement.mode_paiement && (
                                <span className="ml-2 text-gray-500">• {paiement.mode_paiement}</span>
                              )}
                            </p>
                          </div>
                          <Badge className={
                            paiement.statut === 'en_attente' 
                              ? 'bg-yellow-600' 
                              : paiement.statut === 'validé'
                              ? 'bg-green-600'
                              : 'bg-red-600'
                          }>
                            {paiement.statut === 'en_attente' ? 'En attente' : paiement.statut === 'validé' ? 'Validé' : 'Refusé'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === 'presences' && (
        /* Onglet Présences */
        <div className="space-y-6">
          {loadingPresences ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-[#D4A024]" />
            </div>
          ) : presencesStats ? (
            <>
              {/* Résumé global */}
              <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-serif text-white">
                    Statistiques Globales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-[#D4A024]/10 rounded-lg">
                      <div className="text-3xl font-bold text-[#D4A024]">
                        {presencesStats.totaux?.pct_global || 0}%
                      </div>
                      <div className="text-base text-gray-400 mt-1">Total</div>
                      <div className="text-base text-gray-500">
                        {presencesStats.totaux?.presences_total || 0}/{presencesStats.totaux?.events_total || 0}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-amber-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-amber-400">
                        {presencesStats.totaux?.pct_aperos || 0}%
                      </div>
                      <div className="text-base text-gray-400 mt-1">Apéros</div>
                      <div className="text-base text-gray-500">
                        {presencesStats.totaux?.presences_aperos || 0}/{presencesStats.totaux?.total_aperos || 0}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-blue-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-blue-400">
                        {presencesStats.totaux?.pct_repas || 0}%
                      </div>
                      <div className="text-base text-gray-400 mt-1">Repas</div>
                      <div className="text-base text-gray-500">
                        {presencesStats.totaux?.presences_repas || 0}/{presencesStats.totaux?.total_repas || 0}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-purple-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-purple-400">
                        {presencesStats.totaux?.pct_anniversaires || 0}%
                      </div>
                      <div className="text-base text-gray-400 mt-1">Anniversaires</div>
                      <div className="text-base text-gray-500">
                        {presencesStats.totaux?.presences_anniversaires || 0}/{presencesStats.totaux?.total_anniversaires || 0}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Détail par saison */}
              <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-serif text-white">
                    Détail par Saison
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {presencesStats.par_saison && presencesStats.par_saison.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="presences-detail-table">
                        <thead className="bg-[#D4A024]/10">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-[#D4A024]">Saison</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-amber-400">Apéros</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-blue-400">Repas</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-purple-400">Anniv.</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-[#D4A024]">% Saison</th>
                          </tr>
                        </thead>
                        <tbody>
                          {presencesStats.par_saison.map((saison, index) => (
                            <tr 
                              key={saison.saison} 
                              className={`border-b border-[#D4A024]/10 ${index % 2 === 0 ? 'bg-black/20' : ''}`}
                            >
                              <td className="px-4 py-3 text-white font-medium">Saison {saison.saison}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-amber-400">{saison.presences_aperos}/{saison.nb_aperos}</span>
                                <span className="text-gray-500 text-sm ml-1">({saison.pct_aperos}%)</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-blue-400">{saison.presences_repas}/{saison.nb_repas}</span>
                                <span className="text-gray-500 text-sm ml-1">({saison.pct_repas}%)</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-purple-400">{saison.presences_anniversaires}/{saison.nb_anniversaires}</span>
                                <span className="text-gray-500 text-sm ml-1">({saison.pct_anniversaires}%)</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`font-bold text-lg ${
                                  saison.pct_global_saison >= 75 ? 'text-green-400' :
                                  saison.pct_global_saison >= 50 ? 'text-[#D4A024]' :
                                  saison.pct_global_saison >= 25 ? 'text-orange-400' :
                                  'text-red-400'
                                }`}>
                                  {saison.pct_global_saison}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      Aucune donnée de présence enregistrée.
                      <br />
                      <span className="text-sm">Les statistiques apparaîtront une fois les données saisies par l'administrateur.</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="p-8 text-center text-gray-400">
              Impossible de charger les statistiques de présence.
            </div>
          )}
        </div>
      )}

      {/* Onglet Paramètres */}
      {activeTab === 'parametres' && (
        <div className="space-y-6" data-testid="settings-section">
          {/* Informations du compte */}
          <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-xl font-serif text-white flex items-center">
                <User className="w-5 h-5 mr-2 text-[#D4A024]" />
                Informations du compte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#D4A024]" />
                  <div>
                    <p className="text-sm text-gray-400">Adresse email</p>
                    <p className="text-white font-medium">{currentMember?.email || 'Non défini'}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-[#D4A024]" />
                  <div>
                    <p className="text-sm text-gray-400">Nom complet</p>
                    <p className="text-white font-medium">{currentMember?.nom_complet}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sécurité */}
          <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-xl font-serif text-white flex items-center">
                <Lock className="w-5 h-5 mr-2 text-[#D4A024]" />
                Sécurité
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => setShowPasswordModal(true)}
                className="w-full bg-[#7A2020] hover:bg-[#8A3030] text-white font-semibold py-4"
                data-testid="btn-change-password"
              >
                <Lock className="w-5 h-5 mr-2" />
                Changer mon mot de passe
              </Button>
              <p className="text-sm text-gray-400 text-center">
                Nous vous recommandons de changer votre mot de passe régulièrement
              </p>
            </CardContent>
          </Card>

          {/* Face ID / Touch ID */}
          {webAuthnSupported && (
            <Card className="bg-black/40 border-2 border-[#D4A024]/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-xl font-serif text-white flex items-center">
                  <Fingerprint className="w-5 h-5 mr-2 text-[#D4A024]" />
                  Face ID / Touch ID
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingPasskeys ? (
                  <p className="text-gray-400 text-center">Chargement...</p>
                ) : passkeys.length > 0 ? (
                  <div className="space-y-3">
                    {passkeys.map((pk) => (
                      <div key={pk.id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Fingerprint className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="text-white font-medium">{pk.nickname}</p>
                            <p className="text-xs text-gray-400">
                              Ajouté le {new Date(pk.created_at).toLocaleDateString('fr-FR')}
                              {pk.last_used && ` • Dernière utilisation : ${new Date(pk.last_used).toLocaleDateString('fr-FR')}`}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePasskey(pk.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      onClick={handleRegisterPasskey}
                      disabled={registeringPasskey}
                      variant="outline"
                      className="w-full border-[#D4A024]/50 text-[#D4A024] hover:bg-[#D4A024]/10"
                    >
                      <Fingerprint className="w-5 h-5 mr-2" />
                      {registeringPasskey ? 'Enregistrement...' : 'Ajouter un autre appareil'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-400 text-center text-sm">
                      Connectez-vous plus rapidement avec Face ID ou Touch ID
                    </p>
                    <Button
                      onClick={handleRegisterPasskey}
                      disabled={registeringPasskey}
                      className="w-full bg-[#D4A024] hover:bg-[#C8941D] text-[#7A2020] font-semibold py-4"
                      data-testid="btn-register-passkey"
                    >
                      <Fingerprint className="w-5 h-5 mr-2" />
                      {registeringPasskey ? 'Enregistrement...' : 'Activer Face ID / Touch ID'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Déconnexion */}
          <Card className="bg-black/40 border-2 border-red-600/30 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-xl font-serif text-white flex items-center">
                <LogOut className="w-5 h-5 mr-2 text-red-400" />
                Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleLogout}
                className="w-full bg-red-700 hover:bg-red-600 text-white font-semibold py-4"
                data-testid="btn-logout"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Se déconnecter
              </Button>
              <p className="text-sm text-gray-400 text-center mt-3">
                Vous serez redirigé vers la page de connexion
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Signaler un paiement */}
      {showPaiementModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#7A2020] border-2 border-[#D4A024] max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-[#D4A024]/30">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-serif text-[#D4A024] flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Signaler un paiement
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPaiementModal(false)}
                  className="text-[#D4A024] hover:bg-[#D4A024]/10"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-gray-300 text-sm mt-1">
                Le président sera notifié et devra valider ce paiement
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Date */}
              <div>
                <Label className="text-gray-300 mb-2 block">Date du paiement *</Label>
                <Input
                  type="date"
                  value={paiementForm.date_paiement}
                  onChange={(e) => setPaiementForm(prev => ({ ...prev, date_paiement: e.target.value }))}
                  className="bg-black/30 border-[#D4A024]/50 text-white"
                />
              </div>

              {/* Objet */}
              <div>
                <Label className="text-gray-300 mb-2 block">Objet du paiement *</Label>
                <div className="max-h-40 overflow-y-auto border border-[#D4A024]/30 rounded-lg bg-black/50">
                  {['cotisation', 'album', 'tombola', 'anniversaire', 'autres'].map((obj) => (
                    <button
                      key={obj}
                      type="button"
                      onClick={() => setPaiementForm(prev => ({ ...prev, objet: obj }))}
                      className={`w-full text-left px-4 py-3 border-b border-[#D4A024]/10 last:border-b-0 ${
                        paiementForm.objet === obj 
                          ? 'bg-[#D4A024]/30 text-[#D4A024]' 
                          : 'text-white hover:bg-[#D4A024]/10'
                      }`}
                    >
                      {obj.charAt(0).toUpperCase() + obj.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Détail si "autres" */}
              {paiementForm.objet === 'autres' && (
                <div>
                  <Label className="text-gray-300 mb-2 block">Précisez</Label>
                  <Input
                    value={paiementForm.detail}
                    onChange={(e) => setPaiementForm(prev => ({ ...prev, detail: e.target.value }))}
                    placeholder="Détail du paiement..."
                    className="bg-black/30 border-[#D4A024]/50 text-white"
                  />
                </div>
              )}

              {/* Montant */}
              <div>
                <Label className="text-gray-300 mb-2 block">Montant (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paiementForm.montant}
                  onChange={(e) => setPaiementForm(prev => ({ ...prev, montant: e.target.value }))}
                  placeholder="0.00"
                  className="bg-black/30 border-[#D4A024]/50 text-white text-lg"
                />
              </div>

              {/* Caisse (où l'argent a été versé) */}
              <div>
                <Label className="text-gray-300 mb-2 block">Caisse créditée *</Label>
                <div className="max-h-40 overflow-y-auto border border-[#D4A024]/30 rounded-lg bg-black/50">
                  {['Compte bancaire', 'Chez Fabien', 'Chez Jacques', 'PayPal', 'Asso Connect'].map((lieu) => (
                    <button
                      key={lieu}
                      type="button"
                      onClick={() => setPaiementForm(prev => ({ ...prev, endroit: lieu }))}
                      className={`w-full text-left px-4 py-3 border-b border-[#D4A024]/10 last:border-b-0 ${
                        paiementForm.endroit === lieu 
                          ? 'bg-[#D4A024]/30 text-[#D4A024]' 
                          : 'text-white hover:bg-[#D4A024]/10'
                      }`}
                      data-testid={`paiement-caisse-${lieu}`}
                    >
                      {lieu}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode de paiement */}
              <div>
                <Label className="text-gray-300 mb-2 block">Mode de paiement *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['Virement', 'Espèces', 'Chèque', 'CB'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setPaiementForm(prev => ({ ...prev, mode_paiement: mode }))}
                      className={`px-3 py-3 rounded-lg border text-sm font-semibold transition ${
                        paiementForm.mode_paiement === mode
                          ? 'bg-[#D4A024]/30 border-[#D4A024] text-[#D4A024]'
                          : 'bg-black/50 border-[#D4A024]/30 text-white hover:bg-[#D4A024]/10'
                      }`}
                      data-testid={`paiement-mode-${mode}`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {/* Résumé */}
              {paiementForm.objet && paiementForm.montant && paiementForm.endroit && paiementForm.mode_paiement && (
                <div className="bg-[#D4A024]/10 border border-[#D4A024]/30 rounded-lg p-4">
                  <p className="text-[#D4A024] font-semibold mb-2">Résumé :</p>
                  <p className="text-white">
                    Paiement de <span className="text-[#D4A024] font-bold">{paiementForm.montant}€</span> pour{' '}
                    <span className="text-[#D4A024] font-bold">{paiementForm.objet}</span>
                    {paiementForm.objet === 'autres' && paiementForm.detail && ` (${paiementForm.detail})`}
                    , versé à la caisse <span className="text-[#D4A024] font-bold">{paiementForm.endroit}</span>
                    , par <span className="text-[#D4A024] font-bold">{paiementForm.mode_paiement}</span>
                    , le <span className="text-[#D4A024] font-bold">{paiementForm.date_paiement}</span>
                  </p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t border-[#D4A024]/30">
                <Button
                  variant="outline"
                  onClick={() => setShowPaiementModal(false)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  disabled={submittingPaiement}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSubmitPaiement}
                  className="flex-1 bg-green-700 hover:bg-green-600 text-white font-bold"
                  disabled={submittingPaiement || !paiementForm.objet || !paiementForm.montant || !paiementForm.endroit || !paiementForm.mode_paiement}
                >
                  {submittingPaiement ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Signaler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#7A2020] border-2 border-[#D4A024] max-w-md w-full">
            <CardHeader className="border-b border-[#D4A024]/30">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-serif text-[#D4A024] flex items-center">
                  <Lock className="w-5 h-5 mr-2" />
                  Changer le mot de passe
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="text-[#D4A024] hover:bg-[#D4A024]/10"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Mot de passe actuel */}
              <div>
                <Label className="text-gray-300 mb-2 block">Mot de passe actuel *</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="bg-black/30 border-[#D4A024]/50 text-white pr-10"
                    placeholder="Entrez votre mot de passe actuel"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4A024]"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <Label className="text-gray-300 mb-2 block">Nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="bg-black/30 border-[#D4A024]/50 text-white pr-10"
                    placeholder="Minimum 6 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D4A024]"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirmer mot de passe */}
              <div>
                <Label className="text-gray-300 mb-2 block">Confirmer le nouveau mot de passe *</Label>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="bg-black/30 border-[#D4A024]/50 text-white"
                  placeholder="Confirmez le nouveau mot de passe"
                />
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-red-400 text-sm mt-1">Les mots de passe ne correspondent pas</p>
                )}
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t border-[#D4A024]/30">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  disabled={changingPassword}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleChangePassword}
                  className="flex-1 bg-[#D4A024] hover:bg-[#c49020] text-[#7A2020] font-bold"
                  disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || passwordForm.newPassword !== passwordForm.confirmPassword}
                >
                  {changingPassword ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Confirmer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
